// Iter70 — JSON Snapshot Diff Helper.
//
// Purely client-side comparison tool for ARXMENTIS scanner snapshots
// (Iter69 envelopes) or raw scanner result payloads. Lets the
// operator paste two JSON objects side-by-side (or upload two .json
// files) and visualize structural differences across the high-value
// ARXMENTIS fields: identity, safety flags, counts, symbol-level
// decisions / confidence / risk / regime / reason codes, and
// selected structures / option plans.
//
// HARD CONSTRAINTS (asserted by Iter70 tests):
//  * Zero outbound HTTP traffic. No axios import. No fetch call.
//    No XHR object. No beacon call.
//  * No side-effects on mount — the component is purely state-
//    driven, so it does not register any effect hook.
//  * No backend endpoints referenced — neither as text targets nor
//    as callable URLs.
//  * No journal / broker / account / position / scanner /
//    candidates endpoint reference.
//  * Tool only describes differences. It NEVER ranks strategies,
//    declares a "best" candidate, or emits a trade recommendation.
//  * File upload (if used) goes through FileReader.readAsText —
//    fully browser-local, never uploaded anywhere.
//
// Static testid registry (asserted by Iter70 tests):
//   snapshot-diff-side-left
//   snapshot-diff-side-right
//   snapshot-diff-left-textarea
//   snapshot-diff-right-textarea
//   snapshot-diff-left-upload
//   snapshot-diff-right-upload
//   snapshot-diff-left-file-input
//   snapshot-diff-right-file-input
//   snapshot-diff-left-parse-error
//   snapshot-diff-right-parse-error

import React, { useRef, useState } from "react";
import {
  AlertTriangle, ChevronDown, ChevronUp, GitCompareArrows,
  ShieldAlert, FileJson, Eraser, Upload,
} from "lucide-react";

const IDENTITY_KEYS = [
  "module_id", "scanner_version", "source_panel",
  "scanner_endpoint", "created_at", "scanned_at",
  "total_symbols", "snapshot_version",
];

const SAFETY_FLAGS = [
  "read_only", "execution_enabled",
  "journal_mutation_enabled", "broker_execution_enabled",
  "assignment_automation_enabled", "exercise_automation_enabled",
];

const SYMBOL_FIELDS = [
  "status", "decision", "confidence_score", "risk_score",
  "regime", "reason_codes", "warnings",
  "selected_structure", "option_plan", "plain_english", "summary",
];

// Normalize either the Iter69 snapshot envelope OR a raw scanner
// response into a common shape `{ identity, safety, counts, results,
// warnings, raw, source }` so the diff renderer can treat both the
// same way.
function normalizeSnapshot(obj) {
  if (!obj || typeof obj !== "object") {
    return { identity: {}, safety: {}, counts: {}, results: [],
              warnings: [], raw: obj, source: "unknown" };
  }
  const isEnvelope = obj.snapshot_version &&
                       obj.response_result &&
                       typeof obj.response_result === "object";
  const result = isEnvelope ? obj.response_result : obj;
  const identity = {};
  for (const k of IDENTITY_KEYS) {
    if (obj[k] !== undefined) identity[k] = obj[k];
    else if (result && result[k] !== undefined) identity[k] = result[k];
  }
  const safety = {};
  for (const k of SAFETY_FLAGS) {
    if (obj[k] !== undefined) safety[k] = obj[k];
    else if (result && result[k] !== undefined) safety[k] = result[k];
  }
  const counts = (result && typeof result.counts === "object")
    ? result.counts : {};
  const results = (result && Array.isArray(result.results))
    ? result.results : [];
  const warnings = (result && Array.isArray(result.warnings))
    ? result.warnings : [];
  return {
    identity, safety, counts, results, warnings,
    raw: obj,
    source: isEnvelope ? "snapshot_envelope" : "raw_scanner_result",
  };
}

// Compare two flat dicts. Returns three lists: added, removed,
// changed (with from/to). Stable-ordered by key for deterministic
// rendering.
function diffFlat(left, right) {
  const out = { added: [], removed: [], changed: [], unchanged: [] };
  const keys = new Set([...Object.keys(left || {}),
                          ...Object.keys(right || {})]);
  const sorted = Array.from(keys).sort();
  for (const k of sorted) {
    const hasL = Object.prototype.hasOwnProperty.call(left || {}, k);
    const hasR = Object.prototype.hasOwnProperty.call(right || {}, k);
    if (hasL && !hasR) out.removed.push({ key: k, value: left[k] });
    else if (!hasL && hasR) out.added.push({ key: k, value: right[k] });
    else {
      const lv = left[k];
      const rv = right[k];
      if (JSON.stringify(lv) !== JSON.stringify(rv)) {
        out.changed.push({ key: k, from: lv, to: rv });
      } else {
        out.unchanged.push({ key: k, value: lv });
      }
    }
  }
  return out;
}

// Index per-symbol results by symbol for set-based comparison.
function indexBySymbol(results) {
  const m = {};
  for (const r of results || []) {
    if (r && typeof r === "object" && r.symbol) m[r.symbol] = r;
  }
  return m;
}

function diffSymbolResults(leftResults, rightResults) {
  const li = indexBySymbol(leftResults);
  const ri = indexBySymbol(rightResults);
  const all = new Set([...Object.keys(li), ...Object.keys(ri)]);
  const sorted = Array.from(all).sort();
  const out = { added: [], removed: [], changed: [], unchanged: [] };
  for (const sym of sorted) {
    if (li[sym] && !ri[sym]) out.removed.push({ symbol: sym, rec: li[sym] });
    else if (!li[sym] && ri[sym]) out.added.push({ symbol: sym, rec: ri[sym] });
    else {
      const fieldDiffs = [];
      for (const f of SYMBOL_FIELDS) {
        const a = li[sym][f];
        const b = ri[sym][f];
        if (a === undefined && b === undefined) continue;
        if (JSON.stringify(a) !== JSON.stringify(b)) {
          fieldDiffs.push({ field: f, from: a, to: b });
        }
      }
      if (fieldDiffs.length === 0) {
        out.unchanged.push({ symbol: sym });
      } else {
        out.changed.push({ symbol: sym, field_diffs: fieldDiffs });
      }
    }
  }
  return out;
}

function valueToText(v) {
  if (v === undefined) return "—";
  if (v === null) return "null";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// ── Render helpers ────────────────────────────────────────────────────

function DiffSection({ title, testid, children }) {
  return (
    <div
      data-testid={testid}
      className="mb-4 rounded-sm border border-[#26262B] bg-[#08080A]"
    >
      <header className="px-3 py-2 border-b border-[#26262B] text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
        {title}
      </header>
      <div className="px-3 py-2">{children}</div>
    </div>
  );
}

function ChangedRow({ row, testidPrefix }) {
  return (
    <div
      data-testid={`${testidPrefix}-changed-${row.key || row.field}`}
      className="grid grid-cols-1 sm:grid-cols-[120px_1fr_1fr] gap-2 text-[10px] font-mono py-1 border-b border-[#26262B]/40 last:border-0"
    >
      <span className="text-[#FFB800] uppercase tracking-[0.12em]">
        {row.key || row.field}
      </span>
      <span className="text-[#FF3366] break-words">
        − {valueToText(row.from)}
      </span>
      <span className="text-[#00FF88] break-words">
        + {valueToText(row.to)}
      </span>
    </div>
  );
}

function AddedRemovedRow({ row, label, accent, testidPrefix, kind }) {
  return (
    <div
      data-testid={`${testidPrefix}-${kind}-${row.key || row.symbol}`}
      className="grid grid-cols-[120px_1fr] gap-2 text-[10px] font-mono py-1 border-b border-[#26262B]/40 last:border-0"
    >
      <span className={`uppercase tracking-[0.12em] ${accent}`}>
        {label} {row.key || row.symbol}
      </span>
      <span className="text-[#E0E0E0] break-words">
        {valueToText(row.value ?? row.rec)}
      </span>
    </div>
  );
}

function FlatDiff({ diff, testidPrefix }) {
  const empty = diff.added.length === 0 &&
                 diff.removed.length === 0 &&
                 diff.changed.length === 0;
  if (empty) {
    return (
      <div
        data-testid={`${testidPrefix}-no-changes`}
        className="text-[10px] font-mono text-[#5F5F66]"
      >
        No differences.
      </div>
    );
  }
  return (
    <div>
      {diff.changed.map(r => (
        <ChangedRow key={r.key || r.field} row={r} testidPrefix={testidPrefix} />
      ))}
      {diff.added.map(r => (
        <AddedRemovedRow
          key={r.key || r.symbol}
          row={r}
          label="Added"
          accent="text-[#00FF88]"
          testidPrefix={testidPrefix}
          kind="added"
        />
      ))}
      {diff.removed.map(r => (
        <AddedRemovedRow
          key={r.key || r.symbol}
          row={r}
          label="Removed"
          accent="text-[#FF3366]"
          testidPrefix={testidPrefix}
          kind="removed"
        />
      ))}
    </div>
  );
}

function SymbolDiff({ diff }) {
  const empty = diff.added.length === 0 &&
                 diff.removed.length === 0 &&
                 diff.changed.length === 0;
  if (empty) {
    return (
      <div
        data-testid="snapshot-diff-symbols-no-changes"
        className="text-[10px] font-mono text-[#5F5F66]"
      >
        No symbol-level differences.
      </div>
    );
  }
  return (
    <div>
      {diff.changed.map(({ symbol, field_diffs }) => (
        <div
          key={symbol}
          data-testid={`snapshot-diff-symbol-changed-${symbol}`}
          className="mb-2 rounded-sm border border-[#FFB800]/30 bg-[#FFB800]/5 p-2"
        >
          <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#FFB800] mb-1">
            Changed {symbol}
          </div>
          {field_diffs.map(fd => (
            <ChangedRow
              key={fd.field}
              row={fd}
              testidPrefix={`snapshot-diff-symbol-${symbol}`}
            />
          ))}
        </div>
      ))}
      {diff.added.map(({ symbol, rec }) => (
        <div
          key={symbol}
          data-testid={`snapshot-diff-symbol-added-${symbol}`}
          className="mb-2 rounded-sm border border-[#00FF88]/30 bg-[#00FF88]/5 p-2 text-[10px] font-mono"
        >
          <div className="uppercase tracking-[0.14em] text-[#00FF88] mb-1">
            Added {symbol} · {rec.status} · CONF {rec.confidence_score ?? 0}
          </div>
          <div className="text-[#E0E0E0] break-words">
            {valueToText(rec)}
          </div>
        </div>
      ))}
      {diff.removed.map(({ symbol, rec }) => (
        <div
          key={symbol}
          data-testid={`snapshot-diff-symbol-removed-${symbol}`}
          className="mb-2 rounded-sm border border-[#FF3366]/30 bg-[#FF3366]/5 p-2 text-[10px] font-mono"
        >
          <div className="uppercase tracking-[0.14em] text-[#FF3366] mb-1">
            Removed {symbol} · {rec.status} · CONF {rec.confidence_score ?? 0}
          </div>
          <div className="text-[#E0E0E0] break-words">
            {valueToText(rec)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Editor side ───────────────────────────────────────────────────────

function SnapshotEditor({ side, text, setText, onUpload, parseErr }) {
  const inputRef = useRef(null);
  return (
    <div
      data-testid={`snapshot-diff-side-${side}`}
      className="flex flex-col gap-1 min-w-0"
    >
      <div className="flex items-center gap-2 text-[10px] font-mono">
        <FileJson size={11} className="text-[#8A8A93]" />
        <span className="uppercase tracking-[0.14em] text-[#8A8A93]">
          {side === "left" ? "Snapshot A" : "Snapshot B"}
        </span>
        <button
          type="button"
          data-testid={`snapshot-diff-${side}-upload`}
          onClick={() => inputRef.current && inputRef.current.click()}
          className="ml-auto px-2 py-0.5 rounded-sm border border-[#26262B] text-[9px] uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
        >
          <Upload size={9} /> Upload .json
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          data-testid={`snapshot-diff-${side}-file-input`}
          onChange={(e) => {
            const f = e.target.files && e.target.files[0];
            if (!f) return;
            const reader = new FileReader();
            reader.onload = () => onUpload(String(reader.result ?? ""));
            reader.readAsText(f);
            e.target.value = "";
          }}
        />
      </div>
      <textarea
        data-testid={`snapshot-diff-${side}-textarea`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
        rows={10}
        placeholder={`Paste ${side === "left" ? "Snapshot A" : "Snapshot B"} JSON…`}
        className="w-full bg-[#08080A] border border-[#26262B] rounded-sm p-2 text-[10px] font-mono text-[#E0E0E0] focus:outline-none focus:border-[#22D3CC]/50 leading-relaxed"
      />
      {parseErr && (
        <div
          data-testid={`snapshot-diff-${side}-parse-error`}
          className="flex items-start gap-1 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1 rounded-sm"
        >
          <AlertTriangle size={10} className="mt-0.5 shrink-0" />
          <span>{parseErr}</span>
        </div>
      )}
    </div>
  );
}

// ── Tile ──────────────────────────────────────────────────────────────

export default function JsonSnapshotDiffHelper() {
  const [open, setOpen] = useState(false);
  const [leftText, setLeftText] = useState("");
  const [rightText, setRightText] = useState("");
  const [leftErr, setLeftErr] = useState(null);
  const [rightErr, setRightErr] = useState(null);
  const [diff, setDiff] = useState(null);

  function parseSide(text, setErr) {
    if (!text || !text.trim()) {
      setErr("Empty input.");
      return null;
    }
    try {
      setErr(null);
      return JSON.parse(text);
    } catch (e) {
      setErr(`Invalid JSON: ${e.message}`);
      return null;
    }
  }

  function compare() {
    const lp = parseSide(leftText, setLeftErr);
    const rp = parseSide(rightText, setRightErr);
    if (lp === null || rp === null) {
      setDiff(null);
      return;
    }
    const left = normalizeSnapshot(lp);
    const right = normalizeSnapshot(rp);
    const sameModule =
      left.identity.module_id &&
      right.identity.module_id &&
      left.identity.module_id === right.identity.module_id;
    setDiff({
      left, right,
      same_module: !!sameModule,
      identity: diffFlat(left.identity, right.identity),
      safety: diffFlat(left.safety, right.safety),
      counts: diffFlat(left.counts, right.counts),
      symbols: diffSymbolResults(left.results, right.results),
      warnings_left: left.warnings,
      warnings_right: right.warnings,
    });
  }

  function clearAll() {
    setLeftText("");
    setRightText("");
    setLeftErr(null);
    setRightErr(null);
    setDiff(null);
  }

  return (
    <section
      data-testid="snapshot-diff-tile"
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      <button
        type="button"
        data-testid="snapshot-diff-toggle"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <GitCompareArrows size={16} className="text-[#22D3CC]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          Snapshot Diff Helper
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · client-side comparison · diagnostic only · no scoring
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* ── Safety banner ───────────────────────────────────── */}
          <div
            data-testid="snapshot-diff-banner"
            className="px-4 py-2 border-b border-[#26262B] bg-[#22D3CC]/5 text-[#22D3CC] text-[10px] font-mono flex items-center gap-3 flex-wrap"
          >
            <ShieldAlert size={12} />
            <span data-testid="snapshot-diff-banner-client-side">
              Client-side JSON comparison only.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="snapshot-diff-banner-no-backend">
              No backend calls.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="snapshot-diff-banner-no-scanner-execution">
              No scanner execution.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="snapshot-diff-banner-no-recommendation">
              No trade recommendation.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="snapshot-diff-banner-no-journal-write">
              No journal write.
            </span>
            <span className="text-[#22D3CC]/50">·</span>
            <span data-testid="snapshot-diff-banner-no-broker-action">
              No broker action.
            </span>
          </div>

          {/* ── Editors + controls ───────────────────────────────── */}
          <div className="px-4 py-3 border-b border-[#26262B] grid grid-cols-1 lg:grid-cols-2 gap-3">
            <SnapshotEditor
              side="left"
              text={leftText}
              setText={setLeftText}
              onUpload={(s) => setLeftText(s)}
              parseErr={leftErr}
            />
            <SnapshotEditor
              side="right"
              text={rightText}
              setText={setRightText}
              onUpload={(s) => setRightText(s)}
              parseErr={rightErr}
            />
          </div>

          <div className="px-4 py-2 border-b border-[#26262B] flex items-center gap-2 text-[10px] font-mono">
            <button
              type="button"
              data-testid="snapshot-diff-compare"
              onClick={compare}
              className="px-3 py-1 rounded-sm border border-[#22D3CC]/40 bg-[#22D3CC]/10 text-[#22D3CC] uppercase tracking-[0.14em] hover:bg-[#22D3CC]/20 inline-flex items-center gap-1"
            >
              <GitCompareArrows size={10} /> Compare Snapshots
            </button>
            <button
              type="button"
              data-testid="snapshot-diff-clear"
              onClick={clearAll}
              className="px-3 py-1 rounded-sm border border-[#26262B] text-[#8A8A93] uppercase tracking-[0.12em] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
            >
              <Eraser size={10} /> Clear / Reset
            </button>
            <span className="ml-auto text-[#5F5F66] uppercase tracking-[0.12em]">
              Diagnostic only — never picks a side.
            </span>
          </div>

          {/* ── Result ───────────────────────────────────────────── */}
          {!diff && (
            <div
              data-testid="snapshot-diff-empty"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              Paste two JSON payloads above (Iter69 snapshots or raw
              scanner results) and click Compare Snapshots.
            </div>
          )}

          {diff && (
            <div className="px-4 py-3" data-testid="snapshot-diff-result">
              {/* Same/different module banner */}
              {diff.same_module ? (
                <div
                  data-testid="snapshot-diff-module-banner-same"
                  className="mb-3 text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-1 rounded-sm border border-[#00FF88]/30 bg-[#00FF88]/5 text-[#00FF88] inline-block"
                >
                  Same module comparison.
                </div>
              ) : (
                <div
                  data-testid="snapshot-diff-module-banner-different"
                  className="mb-3 text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-1 rounded-sm border border-[#FFB800]/30 bg-[#FFB800]/5 text-[#FFB800] inline-block"
                >
                  Different modules — compare for diagnostics only.
                </div>
              )}

              <DiffSection
                title="1. Snapshot identity"
                testid="snapshot-diff-section-identity"
              >
                <FlatDiff diff={diff.identity}
                            testidPrefix="snapshot-diff-identity" />
              </DiffSection>

              <DiffSection
                title="2. Safety / disclosure flags"
                testid="snapshot-diff-section-safety"
              >
                <FlatDiff diff={diff.safety}
                            testidPrefix="snapshot-diff-safety" />
              </DiffSection>

              <DiffSection
                title="3. Counts"
                testid="snapshot-diff-section-counts"
              >
                <FlatDiff diff={diff.counts}
                            testidPrefix="snapshot-diff-counts" />
              </DiffSection>

              <DiffSection
                title="4. Symbol-level results"
                testid="snapshot-diff-section-symbols"
              >
                <SymbolDiff diff={diff.symbols} />
              </DiffSection>

              <DiffSection
                title="5. Warnings (A vs B)"
                testid="snapshot-diff-section-warnings"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px] font-mono">
                  <div data-testid="snapshot-diff-warnings-left">
                    <div className="text-[#5F5F66] uppercase tracking-[0.12em]">
                      A
                    </div>
                    {diff.warnings_left.length === 0
                      ? <span className="text-[#5F5F66]">—</span>
                      : diff.warnings_left.map((w, i) => (
                        <div key={i} className="text-[#FFB800]">
                          {w}
                        </div>))}
                  </div>
                  <div data-testid="snapshot-diff-warnings-right">
                    <div className="text-[#5F5F66] uppercase tracking-[0.12em]">
                      B
                    </div>
                    {diff.warnings_right.length === 0
                      ? <span className="text-[#5F5F66]">—</span>
                      : diff.warnings_right.map((w, i) => (
                        <div key={i} className="text-[#FFB800]">
                          {w}
                        </div>))}
                  </div>
                </div>
              </DiffSection>

              <details
                data-testid="snapshot-diff-raw-details"
                className="mb-2"
              >
                <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
                  6. Raw JSON (collapsed) — A then B
                </summary>
                <pre
                  data-testid="snapshot-diff-raw-left"
                  className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-64 overflow-auto"
                >
                  {JSON.stringify(diff.left.raw, null, 2)}
                </pre>
                <pre
                  data-testid="snapshot-diff-raw-right"
                  className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-64 overflow-auto"
                >
                  {JSON.stringify(diff.right.raw, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
